import { KeyRound, LockKeyhole } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  type LearnerAccessStatus,
  formatLearnerAccessDate,
} from '@features/premium/shared/learner-access-status'
import { LmsButton } from '@shared/ui'
import './learner-access-required-panel.css'

interface LearnerAccessRequiredPanelProps {
  readonly accessStatus: LearnerAccessStatus
  readonly contextLabel?: string
  readonly onBuyKey: () => void
  readonly onEnterKey: () => void
}

export function LearnerAccessRequiredPanel({
  accessStatus,
  contextLabel,
  onBuyKey,
  onEnterKey,
}: LearnerAccessRequiredPanelProps) {
  const { i18n, t } = useTranslation('translation')
  const expiryCopy = accessStatus.expiresAt
    ? t('Access date: {{date}}', {
        date: formatLearnerAccessDate(accessStatus.expiresAt, i18n.language, t),
      })
    : null

  return (
    <section
      aria-labelledby="learner-access-required-title"
      className="learnerAccessRequiredPanel"
      role="status"
    >
      <div className="learnerAccessRequiredCopy">
        <div className="learnerAccessRequiredBadge">
          <LockKeyhole aria-hidden="true" size={16} strokeWidth={2.6} />
          <span>{accessStatus.label}</span>
        </div>
        <h2 id="learner-access-required-title">
          {contextLabel
            ? t('Unlock {{context}}', { context: contextLabel })
            : t('Unlock protected lessons')}
        </h2>
        <p>{accessStatus.noticeBody}</p>
        {accessStatus.remainingLabel ? (
          <p className="learnerAccessRequiredMeta">
            {accessStatus.remainingLabel}
          </p>
        ) : null}
        {expiryCopy ? (
          <p className="learnerAccessRequiredMeta">
            {expiryCopy}
          </p>
        ) : null}
        <div className="learnerAccessRequiredActions">
          <LmsButton
            leadingIcon={<KeyRound aria-hidden="true" className="size-4" />}
            onClick={onBuyKey}
            variant="primary"
          >
            {t('Buy a key')}
          </LmsButton>
          <LmsButton onClick={onEnterKey} variant="outline">
            {t('Active account')}
          </LmsButton>
        </div>
      </div>
      <div className="learnerAccessRequiredMascot" aria-hidden="true">
        <img alt="" src="/images/hana-unlock-key.png" />
      </div>
    </section>
  )
}
